/**
 * Build proposal-first context for POST /rfp/staff/suggest-assignments.
 */

import {
  mergeProposalStaffAssignments,
  parseProposalStaffAssignments,
  type ParsedStaffAssignment,
} from './parseProposalStaffAssignments';

export interface StaffRequirementRef {
  position_id: string;
  role_title?: string;
}

export interface StaffMemberOption {
  id: string;
  name: string;
}

export function resolveStaffMemberIdByName(
  name: string,
  staffMembers: StaffMemberOption[],
): string | undefined {
  const norm = name.trim().toLowerCase();
  if (!norm) return undefined;
  const exact = staffMembers.find((m) => m.name.toLowerCase() === norm);
  if (exact) return exact.id;
  const prefix = norm.split(',')[0]?.trim() ?? norm;
  const byPrefix = staffMembers.find((m) => {
    const memberName = m.name.toLowerCase();
    return memberName === prefix || memberName.startsWith(prefix) || prefix.startsWith(memberName);
  });
  if (byPrefix) return byPrefix.id;
  return staffMembers.find((m) => {
    const memberName = m.name.toLowerCase();
    return norm.includes(memberName) || memberName.includes(prefix);
  })?.id;
}

export function resolveProposalStaffMemberIds(
  proposalByPosition: Record<string, ParsedStaffAssignment>,
  staffMembers: StaffMemberOption[],
): Record<string, ParsedStaffAssignment & { staff_member_id?: string }> {
  const resolved: Record<string, ParsedStaffAssignment & { staff_member_id?: string }> = {};
  for (const [pid, entry] of Object.entries(proposalByPosition)) {
    const staff_member_id =
      entry.staff_member_id ??
      (entry.name ? resolveStaffMemberIdByName(entry.name, staffMembers) : undefined);
    resolved[pid] = { ...entry, staff_member_id };
  }
  return resolved;
}

export function buildCurrentAssignmentsForSuggestions(
  staffRequirements: StaffRequirementRef[],
  proposalByPosition: Record<string, { staff_member_id?: string }>,
  confirmedByPosition: Record<string, { staff_member_id?: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const req of staffRequirements) {
    const proposalId = proposalByPosition[req.position_id]?.staff_member_id;
    const confirmedId = confirmedByPosition[req.position_id]?.staff_member_id;
    const staffId = proposalId || confirmedId;
    if (staffId) out[req.position_id] = staffId;
  }
  return out;
}

export function buildProposalContentSignature(
  staffRequirements: StaffRequirementRef[],
  proposalByPosition: Record<string, { name?: string }>,
): string {
  return staffRequirements
    .map((req) => `${req.position_id}:${proposalByPosition[req.position_id]?.name ?? ''}`)
    .join('|');
}

export function buildAssignmentsSignature(current: Record<string, string>): string {
  return Object.entries(current)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([pid, sid]) => `${pid}:${sid}`)
    .join('|');
}

export function buildCurrentAssignmentNames(
  staffRequirements: StaffRequirementRef[],
  proposalByPosition: Record<string, ParsedStaffAssignment>,
  proposalByPositionResolved: Record<string, ParsedStaffAssignment & { staff_member_id?: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const req of staffRequirements) {
    if (proposalByPositionResolved[req.position_id]?.staff_member_id) continue;
    const name = proposalByPosition[req.position_id]?.name;
    if (name) out[req.position_id] = name;
  }
  return out;
}

export function mergeProposalFromReportContent(
  v2Content: string | undefined,
  v1Content: string | undefined,
  staffRequirements: StaffRequirementRef[],
): Record<string, ParsedStaffAssignment> {
  const v2Parsed = parseProposalStaffAssignments(v2Content ?? '', staffRequirements);
  const v1Parsed = parseProposalStaffAssignments(v1Content ?? '', staffRequirements);
  return mergeProposalStaffAssignments(v2Parsed, v1Parsed);
}
